import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ShieldCheck } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';
import axios from 'axios';

const MAPRIX_LOGO = '/images/maprix-logo.jpg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const businessTypes = ['Restaurant', 'Cafe', 'Cloud Kitchen', 'Catering', 'Hotel', 'Other'];
const productOptions = [
  'Red Chilli Powder', 'Turmeric Powder', 'Coriander Powder', 'Garam Masala',
  'Kanda Lasun Masala', 'Kitchen King Masala', 'Chicken Masala', 'Custom Blend',
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    full_name: '', company_name: '', business_type: '', city: '',
    phone: '', product_interest: [], message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleProduct = (product) => {
    setFormData(prev => ({
      ...prev,
      product_interest: prev.product_interest.includes(product)
        ? prev.product_interest.filter(p => p !== product)
        : [...prev.product_interest, product],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.company_name || !formData.city || !formData.phone) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, { ...formData, source_page: 'contact' });
      setSubmitted(true);
      setFormData({ full_name: '', company_name: '', business_type: '', city: '', phone: '', product_interest: [], message: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={TESTIDS.CONTACT_PAGE}>
      {/* ===== PAGE HERO ===== */}
      <section className="bg-[#1B3A6B] py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-[#C9922C] mb-4">Get In Touch</p>
            <h1 className="font-playfair text-[#F9F3E8] text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Let's Cook Up<br />a Partnership
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTACT LAYOUT ===== */}
      <section className="bg-[#F9F3E8] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Left: Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <h2 className="font-playfair text-[#1B3A6B] text-2xl md:text-3xl mb-8">Contact Details</h2>

              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full border border-[#C9922C]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#C9922C]" />
                  </div>
                  <div>
                    <p className="font-lato font-semibold text-[#1B3A6B] mb-0.5">Maprix Industries Pvt. Ltd.</p>
                    <p className="font-lato text-[#5C3D11] text-sm">MIDC Jalgaon, Maharashtra</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full border border-[#C9922C]/30 flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#C9922C]" />
                  </div>
                  <div>
                    <p className="font-lato font-semibold text-[#1B3A6B] mb-0.5">Phone</p>
                    <a href="tel:+917774926163" className="font-lato text-[#5C3D11] text-sm hover:text-[#C9922C] transition-colors">
                      +91-7774926163
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full border border-[#C9922C]/30 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#C9922C]" />
                  </div>
                  <div>
                    <p className="font-lato font-semibold text-[#1B3A6B] mb-0.5">Email</p>
                    <a href="mailto:maprixindustries115@gmail.com" className="font-lato text-[#5C3D11] text-sm hover:text-[#C9922C] transition-colors">
                      maprixindustries115@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full border border-[#C9922C]/30 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck size={18} className="text-[#C9922C]" />
                  </div>
                  <div>
                    <p className="font-lato font-semibold text-[#1B3A6B] mb-0.5">FSSAI License</p>
                    <p className="font-lato text-[#5C3D11] text-sm">21524031001278</p>
                  </div>
                </div>
              </div>

              <img src={MAPRIX_LOGO} alt="Maprix Industries" className="h-16 rounded opacity-80" />
            </motion.div>

            {/* Right: Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-3"
            >
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  data-testid={TESTIDS.FORM_SUCCESS}
                  className="bg-white p-12 border border-[#C9922C]/20 text-center"
                >
                  <p className="font-playfair text-[#1B3A6B] text-3xl mb-3">Thank You!</p>
                  <p className="font-lato text-[#5C3D11]">Your enquiry has been submitted successfully. Our team will reach out to you within 24 hours.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="btn-navy mt-6"
                  >
                    Send Another Enquiry
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 border border-[#C9922C]/15 shadow-lg">
                  <h3 className="font-playfair text-[#1B3A6B] text-xl mb-8">Send Enquiry</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">Full Name *</label>
                      <input
                        data-testid={TESTIDS.FORM_NAME}
                        type="text" required
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="brand-input"
                      />
                    </div>
                    <div>
                      <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">Company Name *</label>
                      <input
                        data-testid={TESTIDS.FORM_COMPANY}
                        type="text" required
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="brand-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">Business Type</label>
                      <select
                        data-testid={TESTIDS.FORM_BUSINESS_TYPE}
                        value={formData.business_type}
                        onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                        className="brand-select"
                      >
                        <option value="">Select type...</option>
                        {businessTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">City *</label>
                      <input
                        data-testid={TESTIDS.FORM_CITY}
                        type="text" required
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="brand-input"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">Phone Number *</label>
                    <input
                      data-testid={TESTIDS.FORM_PHONE}
                      type="tel" required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="brand-input"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-2 block">Product Interest</label>
                    <div className="flex flex-wrap gap-2" data-testid={TESTIDS.FORM_PRODUCT_INTEREST}>
                      {productOptions.map(p => (
                        <button
                          key={p} type="button"
                          onClick={() => toggleProduct(p)}
                          className={`text-xs font-lato px-3 py-1.5 border transition-all duration-200 ${
                            formData.product_interest.includes(p)
                              ? 'bg-[#1B3A6B] text-[#F9F3E8] border-[#1B3A6B]'
                              : 'bg-transparent text-[#5C3D11] border-[#5C3D11]/25 hover:border-[#C9922C]'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <label className="font-cinzel text-[10px] tracking-wider uppercase text-[#5C3D11]/70 mb-1 block">Message / Special Requirements</label>
                    <textarea
                      data-testid={TESTIDS.FORM_MESSAGE}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="brand-textarea"
                      rows={4}
                    />
                  </div>

                  <button
                    data-testid={TESTIDS.FORM_SUBMIT}
                    type="submit"
                    disabled={submitting}
                    className="btn-gold w-full disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send Enquiry'}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
